import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PackageCard } from "@/components/PackageCard";
import { GraduationCap } from "@phosphor-icons/react";

const LEVEL_ORDER = ["Pre-K", "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5"];

export default function Catalog() {
  const [packages, setPackages] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const sortLevels = (arr) =>
    [...arr].sort((a, b) => {
      const ia = LEVEL_ORDER.indexOf(a);
      const ib = LEVEL_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

  const allLevels = sortLevels([...new Set(packages.map((p) => p.level))]);
  const visibleLevels = filter === "All" ? allLevels : allLevels.filter((l) => l === filter);
  const chips = ["All", ...allLevels];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="catalog-page">
      <div className="mb-8">
        <span className="font-body font-semibold text-brand-coral uppercase tracking-[0.2em] text-xs">The full shelf</span>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-ink tracking-tight mt-1">Reading Packages</h1>
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-sky/10 border-2 border-brand-sky/20" data-testid="level-range-badge">
          <GraduationCap size={18} weight="duotone" className="text-brand-sky" />
          <span className="font-body font-bold text-sm text-brand-sky">Pre-K – 5th Grade · Complete Reading Curriculum</span>
        </div>
        <p className="font-body text-ink-soft mt-3 text-lg max-w-2xl leading-relaxed">
          Browse by grade level. Each package is a one-time purchase, packed with interactive lessons your child can access online forever.
        </p>
      </div>

      {/* Grade filter */}
      <div className="flex flex-wrap gap-2 mb-12 sticky top-16 z-30 py-3 -mx-2 px-2 backdrop-blur-xl bg-background/70 rounded-2xl" data-testid="grade-filter">
        {chips.map((c) => {
          const active = filter === c;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 rounded-full font-body font-semibold text-sm transition-transform hover:-translate-y-0.5 border-2 ${
                active ? "bg-brand-sky text-white border-brand-sky" : "bg-white text-ink border-slate-900/10 hover:border-brand-sky"
              }`}
              data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {c}
            </button>
          );
        })}
      </div>

      <div className="space-y-16">
        {visibleLevels.map((level) => {
          const group = packages.filter((p) => p.level === level);
          const color = group[0]?.color || "#0ea5e9";
          return (
            <section key={level} data-testid={`level-section-${level.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-3 mb-6">
                <span className="grid place-items-center w-11 h-11 rounded-2xl" style={{ backgroundColor: `${color}20` }}>
                  <GraduationCap size={24} weight="duotone" style={{ color }} />
                </span>
                <div>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-ink tracking-tight">{level}</h2>
                  <p className="font-body text-sm text-ink-soft">{group.length} package{group.length > 1 ? "s" : ""}</p>
                </div>
                <span className="flex-1 h-0.5 rounded-full ml-3" style={{ backgroundColor: `${color}25` }} />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.map((p, i) => (
                  <PackageCard key={p.id} pkg={p} index={i} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
